import { operationsClient } from 'packages/lib/api/annotations-ops/http-client';
import {
  costGroupResponseZod,
  CostGroupResponse,
  CostQuery,
  CostQueryFilters,
  CostSeriesQuery,
  CostSeriesResponse,
  costSeriesResponseZod,
} from 'packages/lib/api/annotations-ops/contract';

/**
 * `taskType` is repeated once per selected type and omitted when the Task Types
 * menu is on "All Task Types". `managerEmail` is only sent for Direct Reports.
 *
 * Built by hand because `HttpClient`'s `query` option accepts one value per key.
 */
const appendCostFilters = (
  params: URLSearchParams,
  { startDate, endDate, taskTypes, managerEmail }: CostQueryFilters
): void => {
  params.set('startDate', startDate);
  params.set('endDate', endDate);
  taskTypes.forEach((taskType) => params.append('taskType', taskType));
  if (managerEmail) params.set('managerEmail', managerEmail);
};

/**
 * The group being viewed goes in `tokenOrId`. All Pipelines and All Requests omit it.
 * `includeAllTasks` is only sent when the Show Only Tasks filter is on.
 */
const buildCostGroupQueryString = (query: CostQuery): string => {
  const params = new URLSearchParams({ groupType: query.groupType });
  if (query.tokenOrId) params.set('tokenOrId', query.tokenOrId);
  appendCostFilters(params, query);
  if (query.includeAllTasks) params.set('includeAllTasks', 'true');
  return params.toString();
};

const buildCostSeriesQueryString = (query: CostSeriesQuery): string => {
  const params = new URLSearchParams({ type: query.type });
  if ('tokenOrId' in query) params.set('tokenOrId', query.tokenOrId);
  if (query.type !== 'Task') appendCostFilters(params, query);
  return params.toString();
};

/** Live `GET /cost/group` and `GET /cost/time-series` on annotations-app-api. */
export const costApi = {
  /**
   * One row per child of the requested group, or one row per task when
   * `includeAllTasks` is set, plus a page summary.
   */
  getGroup: (query: CostQuery): Promise<CostGroupResponse> =>
    operationsClient.get(`/cost/group?${buildCostGroupQueryString(query)}`, {
      schema: costGroupResponseZod,
    }),

  /** One point per day for every chart series. */
  getTimeSeries: (query: CostSeriesQuery): Promise<CostSeriesResponse> =>
    operationsClient.get(
      `/cost/time-series?${buildCostSeriesQueryString(query)}`,
      { schema: costSeriesResponseZod }
    ),
};
